import chalk from "chalk";
import { existsSync, readFileSync } from "node:fs";
import { join } from "node:path";
import type { DriftReport, DriftIssue, Severity } from "./types.js";
import { specFor } from "./drift/issue-codes.js";

// ─── Symbol + colour system (never colour alone) ─────────────────────────────
const icon = {
  error:   chalk.red("✖"),
  warning: chalk.yellow("⚠"),
  info:    chalk.cyan("ℹ"),
  ok:      chalk.green("✔"),
};

const severityColor: Record<Severity, (s: string) => string> = {
  error:   chalk.red,
  warning: chalk.yellow,
  info:    chalk.cyan,
};

// ─── Label / advice / delta come from the IssueCode registry ────────────────
function getAdvice(issue: DriftIssue): string | null {
  return specFor(issue.code).advice(issue) || issue.suggestion || null;
}

function getDelta(issue: DriftIssue): { before: string; after: string } | null {
  return specFor(issue.code).delta?.(issue) ?? null;
}

// ─── Main console reporter ────────────────────────────────────────────────────
export function reportConsole(
  report: DriftReport,
  opts: { verbose?: boolean } = {}
): void {
  if (opts.verbose && report.diagnostics) {
    printDiagnostics(report);
    console.log();
  }

  const pathRoot = report.subProjectRoot !== report.projectRoot
    ? `${report.subProjectRoot} ${chalk.dim(`(sub-root of ${report.projectRoot})`)}`
    : report.projectRoot;
  console.log(chalk.dim(`  Checking paths relative to: ${pathRoot}`));
  console.log();

  if (report.issues.length === 0) {
    printSummary(report);
    return;
  }

  const grouped = groupByFile(report.issues);

  for (const [file, issues] of Object.entries(grouped)) {
    const errorCount  = issues.filter((i) => i.severity === "error").length;
    const warnCount   = issues.filter((i) => i.severity === "warning").length;
    const countParts  = [
      errorCount  ? chalk.red(`${errorCount} error${errorCount !== 1 ? "s" : ""}`) : "",
      warnCount   ? chalk.yellow(`${warnCount} warning${warnCount !== 1 ? "s" : ""}`) : "",
    ].filter(Boolean).join(chalk.dim(", "));
    console.log(chalk.bold(file) + chalk.dim("  ") + countParts);

    for (const issue of issues) {
      const sev   = issue.severity as Severity;
      const sym   = icon[sev === "info" ? "info" : sev];
      const label = specFor(issue.code).label;
      console.log(`  ${sym} ${severityColor[sev](label)}`);

      // Before/After delta
      const delta = getDelta(issue);
      if (delta) {
        console.log(`     ${chalk.dim("scaffold says")}  ${chalk.red(delta.before)}`);
        console.log(`     ${chalk.dim("reality is   ")}  ${chalk.yellow(delta.after)}`);
      } else if (issue.message && issue.message !== label) {
        // Show the raw message as a subtitle when it adds info
        console.log(`     ${chalk.dim(issue.message)}`);
      }

      // Git context — shows *why* the file is missing (deleted/renamed in commit X)
      if (issue.gitContext) {
        const gc = issue.gitContext;
        const reason = gc.renamedTo
          ? `renamed → ${chalk.cyan(gc.renamedTo)}`
          : `deleted`;
        console.log(
          `     ${chalk.dim("history")}        ${reason} in ${chalk.cyan(gc.commit)} ${chalk.dim(`(${gc.ago})`)} ${chalk.dim(gc.message)}`
        );
      }

      // Advice (shown by default — not just in --verbose)
      const advice = getAdvice(issue);
      if (advice) {
        console.log(`     ${icon.info} ${chalk.dim(advice)}`);
      }

      // Verbose extras
      if (opts.verbose && issue.claim) {
        console.log(
          chalk.dim(`     claim: kind=${issue.claim.kind}, intent=${issue.claim.intent}, confidence=${issue.claim.confidence}`)
        );
      }
    }
    console.log();
  }

  printSummary(report);
}

export function reportQuiet(report: DriftReport): void {
  const errors   = report.issues.filter((i) => i.severity === "error").length;
  const warnings = report.issues.filter((i) => i.severity === "warning").length;
  const parts: string[] = [];
  if (errors)   parts.push(`${errors} error${errors > 1 ? "s" : ""}`);
  if (warnings) parts.push(`${warnings} warning${warnings > 1 ? "s" : ""}`);
  const detail = parts.length ? ` (${parts.join(", ")})` : "";
  const color  = report.score >= 80 ? chalk.green : report.score >= 50 ? chalk.yellow : chalk.red;
  console.log(`CAI: drift score ${color(`${report.score}/100`)}${detail}`);
}

export function reportJSON(report: DriftReport): void {
  console.log(JSON.stringify(report, null, 2));
}

export function reportExplain(report: DriftReport, query: string): void {
  const normalized = query.trim().toLowerCase();
  const exactCodeMatches = report.issues.filter(
    (issue) => issue.code.toLowerCase() === normalized
  );
  const matches = exactCodeMatches.length > 0
    ? exactCodeMatches
    : report.issues.filter((issue) =>
        issue.code.toLowerCase().includes(normalized) ||
        issue.file.toLowerCase().includes(normalized) ||
        issue.message.toLowerCase().includes(normalized)
      );

  if (matches.length === 0) {
    const knownCodes = [...new Set(report.issues.map((issue) => issue.code))].sort();
    console.log(chalk.bold(`No drift issues matched "${query}".`));
    if (knownCodes.length > 0) {
      console.log(chalk.dim(`Available issue codes: ${knownCodes.join(", ")}`));
    }
    return;
  }

  console.log(chalk.bold(`Explain: ${query}`));
  console.log(chalk.dim(`${matches.length} matching issue${matches.length === 1 ? "" : "s"}`));
  console.log();

  for (const issue of matches) {
    const sev   = issue.severity as Severity;
    const sym   = icon[sev === "info" ? "info" : sev];
    const label = specFor(issue.code).label;
    const loc   = issue.line ? `:${issue.line}` : "";

    console.log(`${sym} ${severityColor[sev](label)}  ${chalk.dim(issue.file + loc)}`);
    console.log(`  ${chalk.dim(issue.message)}`);

    const delta = getDelta(issue);
    if (delta) {
      console.log(`  ${chalk.dim("scaffold says")}  ${chalk.red(delta.before)}`);
      console.log(`  ${chalk.dim("reality is   ")}  ${chalk.yellow(delta.after)}`);
    }

    const advice = getAdvice(issue);
    if (advice) console.log(`  ${icon.info} ${chalk.dim(advice)}`);

    if (issue.claim) {
      console.log(
        chalk.dim(`  claim: ${issue.claim.value} [kind=${issue.claim.kind}, origin=${issue.claim.origin}, intent=${issue.claim.intent}, confidence=${issue.claim.confidence}]`)
      );
    }
    console.log();
  }
}

// ─── Summary bar ─────────────────────────────────────────────────────────────
function printSummary(report: DriftReport): void {
  const errors   = report.issues.filter((i) => i.severity === "error").length;
  const warnings = report.issues.filter((i) => i.severity === "warning").length;
  const color    = report.score >= 80 ? chalk.green : report.score >= 50 ? chalk.yellow : chalk.red;

  console.log(
    chalk.bold(`  Drift score: ${color(`${report.score}/100`)}`) +
    chalk.dim(`  ·  ${report.filesChecked} file${report.filesChecked !== 1 ? "s" : ""} checked`)
  );

  // Show weighted score only when telemetry actually contributed insight
  if (report.usedTelemetry && typeof report.weightedScore === "number" && report.weightedScore !== report.score) {
    const wColor = report.weightedScore >= 80 ? chalk.green : report.weightedScore >= 50 ? chalk.yellow : chalk.red;
    console.log(
      chalk.dim(`  Hot-path weighted: `) + wColor(`${report.weightedScore}/100`) +
      chalk.dim(`  · counts queries from last 7 days`)
    );
  }

  if (errors === 0 && warnings === 0) {
    console.log(`  ${icon.ok} ${chalk.green("Scaffold is accurate — no drift detected.")}`);
  } else if (errors > 0) {
    console.log(
      chalk.dim(
        `  ${errors} error${errors !== 1 ? "s" : ""}${warnings > 0 ? `, ${warnings} warning${warnings !== 1 ? "s" : ""}` : ""}` +
        ` — some scaffold files have drifted from the codebase.`
      )
    );
    console.log(
      chalk.dim(`  → `) + chalk.white("cai fix") + chalk.dim(" for safe auto-fixes  ·  ") +
      chalk.white("cai sync") + chalk.dim(" to let AI update what's broken")
    );
  } else {
    console.log(chalk.dim(`  ${warnings} warning${warnings !== 1 ? "s" : ""} — scaffold may be slightly out of date.`));
    console.log(chalk.dim(`  → `) + chalk.white("cai sync --warnings") + chalk.dim(" to address them"));
  }

  // Surface recurring pattern hint when correction clusters detected
  const recurringCount = report.issues.filter((i) => i.code === "RECURRING_CORRECTION").length;
  if (recurringCount > 0) {
    console.log(
      chalk.dim(`  ℹ `) + chalk.dim(`${recurringCount} recurring correction pattern${recurringCount !== 1 ? "s" : ""} detected — run `) +
      chalk.white("cai pattern recurring") + chalk.dim(" to auto-generate patterns")
    );
  }

  // Cross-link: surface trend hint after a few runs
  printTrendHint();
}

/** Show a one-line trend hint when at least 3 runs of history exist. */
function printTrendHint(): void {
  try {
    const path = join(process.cwd(), ".cai", ".cache", "drift-history.jsonl");
    if (!existsSync(path)) return;
    const lines = readFileSync(path, "utf8").split("\n").filter(Boolean);
    if (lines.length < 3) return;
    console.log(chalk.dim(`  → `) + chalk.white("cai check --history") + chalk.dim(` to see how the score is trending (${lines.length} runs)`));
  } catch {
    // ignore — never block the report over a hint
  }
}

// ─── Verbose diagnostics ──────────────────────────────────────────────────────
function printDiagnostics(report: DriftReport): void {
  const diagnostics = report.diagnostics;
  if (!diagnostics) return;

  console.log(chalk.bold("  Verbose diagnostics"));
  console.log();

  console.log(chalk.dim("  Scaffold files scanned"));
  for (const file of diagnostics.scaffoldFiles) {
    console.log(`    ${chalk.dim("·")} ${file}`);
  }

  console.log();
  console.log(chalk.dim("  Claims extracted"));
  for (const [kind, count] of Object.entries(diagnostics.claimsByKind)) {
    console.log(`    ${chalk.dim("·")} ${kind}: ${count}`);
  }

  console.log();
  console.log(chalk.dim("  Project model"));
  const manifestLabel = diagnostics.project.rootManifestType
    ? diagnostics.project.rootManifestName
      ? `${diagnostics.project.rootManifestType} (${diagnostics.project.rootManifestName})`
      : diagnostics.project.rootManifestType
    : "none";
  console.log(`    ${chalk.dim("·")} root manifest: ${manifestLabel}`);
  console.log(`    ${chalk.dim("·")} workspaces: ${diagnostics.project.workspaceCount}, internal deps: ${diagnostics.project.workspaceDependencyCount}`);
  if (diagnostics.project.workspaceNames.length > 0) {
    console.log(`    ${chalk.dim("·")} workspace sample: ${diagnostics.project.workspaceNames.join(", ")}`);
  }
  console.log(`    ${chalk.dim("·")} commands discovered: ${diagnostics.project.commandCount}`);
  if (diagnostics.project.sampleCommands.length > 0) {
    console.log(`    ${chalk.dim("·")} command sample: ${diagnostics.project.sampleCommands.join(", ")}`);
  }

  console.log();
  console.log(chalk.dim("  Checker summary"));
  for (const summary of diagnostics.checkerSummaries) {
    console.log(`    ${chalk.dim("·")} ${summary.name}: ${summary.checked} checked, ${summary.issuesFound} issues`);
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function groupByFile(issues: DriftIssue[]): Record<string, DriftIssue[]> {
  const grouped: Record<string, DriftIssue[]> = {};
  for (const issue of issues) {
    if (!grouped[issue.file]) grouped[issue.file] = [];
    grouped[issue.file].push(issue);
  }
  return grouped;
}
